/* global trapFocus, removeTrapFocus */

if (!customElements.get('hotspots-image')) {
  class HotspotsImage extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.handleClick.bind(this));
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Handles 'click' events on the hotspot images.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.hotspot__btn')) return;
      const hotspot = evt.target.parentNode;

      if (hotspot === this.activeHotspot) {
        this.closeActiveHotspot(evt);
      } else {
        if (this.activeHotspot) this.closeActiveHotspot(evt);
        this.activateHotspot(hotspot);
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleClickOutside(evt) {
      // Keep the hotspot open when clicking anywhere inside it (button, info card,
      // or an interactive product card / quick-buy). Only close on true outside clicks.
      if (evt.target.closest('.hotspot')) return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    /**
     * Handles 'keyup' events on the document.
     * @param {object} evt - Event object.
     */
    handleKeyup(evt) {
      if (evt.key !== 'Escape') return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    /**
     * Handles 'transitionend' events on the shoppable image.
     * @param {object} evt - Event object.
     */
    handleTransitionEnd(evt) {
      if (this.activeHotspot || evt.propertyName !== 'opacity') return;

      this.classList.add('overflow-hidden');
      this.activeCard.style = null;
      this.activeCard = null;

      this.removeEventListener('transitionend', this.transitionHandler);
    }

    /**
     * Activates a hotspot and shows the corresponding product card.
     * @param {Element} hotspot - Selected hotspot.
     */
    activateHotspot(hotspot) {
      this.activeCard = hotspot.querySelector('.hotspot__card');
      this.checkCardPosition();
      this.classList.remove('overflow-hidden');
      this.setHotspotActiveState(hotspot, true);
      trapFocus(hotspot);

      this.clickOutsideHandler = this.clickOutsideHandler || this.handleClickOutside.bind(this);
      this.keyUpHandler = this.handleKeyup || this.handleKeyup.bind(this);
      this.resizeHandler = this.resizeHandler || this.closeActiveHotspot.bind(this);

      document.addEventListener('click', this.clickOutsideHandler);
      document.addEventListener('keyup', this.keyUpHandler);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Hides the product card and deactivates the hotspot.
     * @param {object} evt - Event object.
     */
    closeActiveHotspot(evt) {
      if (evt.type !== 'on:debounced-resize') {
        this.transitionHandler = this.transitionHandler || this.handleTransitionEnd.bind(this);
        this.addEventListener('transitionend', this.transitionHandler);
      }

      if (this.activeHotspot) {
        this.setHotspotActiveState(this.activeHotspot, false);
        removeTrapFocus();

        if (evt.type === 'on:debounced-resize') {
          this.classList.add('overflow-hidden');
          this.activeCard.style = null;
          this.activeCard = null;
        }
      }

      document.removeEventListener('click', this.clickOutsideHandler);
      document.removeEventListener('keyup', this.keyUpHandler);
    }

    /**
     * Ensures the card is within the image bounds horizontally and viewport bounds vertically.
     */
    checkCardPosition() {
      // Product cards are larger than the info cards this section was built for, so they get
      // their own placement routine that keeps them fully inside the image on every device.
      if (this.activeCard.classList.contains('hotspot__card--product')) {
        this.positionProductCard();
        return;
      }

      let cardRect;

      // Card prefers centered on mobile, although must fit
      if (!theme.mediaMatches.md) {
        const buttonRect = this.activeCard.parentElement.getBoundingClientRect();
        const imageRect = this.getBoundingClientRect();
        const rightOffset = this.activeCard.clientWidth / 2;
        this.activeCard.style.right = `calc(50% - ${rightOffset}px)`;
        cardRect = this.activeCard.getBoundingClientRect();

        const naturalLeft = buttonRect.left + buttonRect.width / 2 - cardRect.width / 2;
        if (naturalLeft < 10) {
          this.activeCard.style.right = `calc(50% - ${rightOffset - naturalLeft}px - var(--gutter))`;
        } else {
          const naturalRight = buttonRect.left + buttonRect.width / 2 + cardRect.width / 2;
          if (naturalRight > imageRect.width - 10) {
            this.activeCard.style.right = `calc(50% - ${rightOffset - (naturalRight - imageRect.width)}px + var(--gutter))`;
          }
        }
      } else {
        cardRect = this.activeCard.getBoundingClientRect();
      }

      if (cardRect.bottom > document.documentElement.clientHeight) {
        // Card is out of the viewport to the bottom.
        this.activeCard.style.top = 'auto';
        this.activeCard.style.bottom = 'calc(100% + 12px)';
      }
    }

    /**
     * Places a product card so it always sits fully inside the image.
     * Desktop: beside the marker, opening toward the image centre.
     * Mobile: centred on the marker. Both axes are clamped to the image bounds.
     */
    positionProductCard() {
      const card = this.activeCard;
      const marker = card.parentElement; // .hotspot

      // Clear any previous inline placement so measurements are clean.
      card.style.top = '';
      card.style.bottom = '';
      card.style.left = '';
      card.style.right = '';
      card.style.transform = 'none';

      const imageRect = this.getBoundingClientRect();
      const markerRect = marker.getBoundingClientRect();
      const cardW = card.offsetWidth;
      const cardH = card.offsetHeight;
      const margin = 12; // keep the card off the very edge
      const gap = 14; // space between marker and card

      // Marker centre, expressed relative to the image's top-left.
      const markerCentreX = markerRect.left - imageRect.left + markerRect.width / 2;
      const markerCentreY = markerRect.top - imageRect.top + markerRect.height / 2;

      // --- Horizontal ---
      let left;
      if (theme.mediaMatches.md) {
        // Open toward the centre: left of the marker if it's past the halfway line, else right.
        if (markerCentreX > imageRect.width / 2) {
          left = markerCentreX - markerRect.width / 2 - gap - cardW;
        } else {
          left = markerCentreX + markerRect.width / 2 + gap;
        }
      } else {
        left = markerCentreX - cardW / 2;
      }
      // Clamp within the image, unless the card is wider than the image (then centre it).
      if (cardW + margin * 2 >= imageRect.width) {
        left = (imageRect.width - cardW) / 2;
      } else {
        left = Math.max(margin, Math.min(left, imageRect.width - cardW - margin));
      }

      // --- Vertical ---
      let top = markerCentreY - cardH / 2; // centre on the marker
      if (cardH + margin * 2 >= imageRect.height) {
        top = (imageRect.height - cardH) / 2;
      } else {
        top = Math.max(margin, Math.min(top, imageRect.height - cardH - margin));
      }

      // Convert image-relative coordinates to marker-relative (the card's offset parent).
      const markerLeftInImage = markerRect.left - imageRect.left;
      const markerTopInImage = markerRect.top - imageRect.top;
      card.style.left = `${Math.round(left - markerLeftInImage)}px`;
      card.style.top = `${Math.round(top - markerTopInImage)}px`;
      card.style.right = 'auto';
      card.style.bottom = 'auto';
    }

    /**
     * Sets the active state of a hotspot.
     * @param {Element} hotspot - Hotspot element.
     * @param {boolean} active - Set the hotspot as active.
     */
    setHotspotActiveState(hotspot, active) {
      hotspot.classList.toggle('is-active', active);
      hotspot.querySelector('.hotspot__btn').setAttribute('aria-expanded', active);
      hotspot.querySelector('.hotspot__card').setAttribute('aria-hidden', !active);

      this.activeHotspot = active ? hotspot : null;
    }
  }

  customElements.define('hotspots-image', HotspotsImage);
}